import Head from "next/head";
import {
  getLandingPageParams,
  getLandingPageConfig,
  getLandingPageUrl,
} from "@/constastants/landingPages";
import { buildLandingPageSchema } from "@/constastants/JsonLdSchemas";
import MetaTags from "@/components/seo/meta-tags";
import SupplierLandingTemplate from "@/components/landing-pages/SupplierLandingTemplate";
import ProductLandingTemplate from "@/components/landing-pages/ProductLandingTemplate";

const TEMPLATES = {
  supplier: SupplierLandingTemplate,
  product: ProductLandingTemplate,
};

export default function LandingPage({ config, canonical, schema }) {
  const Template = TEMPLATES[config.template] ?? SupplierLandingTemplate;

  return (
    <>
      {/*
        SEO for all 21 landing pages, wired in the ONE route they share.

        ⚠️ `NoIndexMetaKey={false}` is REQUIRED and is not the default.
        `MetaTags` defaults it to `true`, and `NoIndexMeta` emits
        `<meta name="robots" content="noindex">` when
        NEXT_PUBLIC_APPLICATION_ON === "PROD" — i.e. omitting this would noindex
        every landing page in production only, while looking perfectly fine
        locally. These pages exist to be indexed.
      */}
      {/*
        Same composition every other SEO page on the site uses (see
        pages/product/index.jsx and pages/product/[singleProduct]/index.jsx):
        `MetaTags` for title/description/OG/Twitter/canonical, then a separate
        <Head> for the JSON-LD only. Canonical is deliberately left to MetaTags
        rather than emitted here, so these pages behave exactly like the rest of
        the site.

        ⚠️ Known limitation, inherited and NOT introduced here: MetaTags assigns
        its canonical inside a useEffect, so the server-rendered HTML carries
        `<link rel="canonical" href="">` and the real URL only appears after
        hydration — on every page of the site, not just these. `defaultCanonical`
        does not change that (verified on /faqs, which passes it and still ships an
        empty href). Fixing it means editing a component shared by most of the
        site, so it is deliberately out of scope for this change and recorded in
        the plan as a follow-up.
      */}
      <MetaTags
        title={config.seo?.title}
        metaTitle={config.seo?.metaTitle}
        description={config.seo?.description}
        defaultCanonical={canonical}
        NoIndexMetaKey={false}
      />

      <Head>
        {config.seo?.keywords?.length > 0 && (
          <meta name="keywords" content={config.seo.keywords.join(", ")} />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <script
          type="application/ld+json"
          // Serialised at build time from the config; no user input reaches it.
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      </Head>

      <Template config={config} />
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: getLandingPageParams().map((params) => ({ params })),
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const config = getLandingPageConfig(params.region, params.slug);
  if (!config) return { notFound: true };

  // Built from REGION/SLUG values via the registry helper, never hardcoded, so
  // the URL scheme lives in exactly one place.
  const canonical = getLandingPageUrl(config.region, config.slug);

  // Self-canonical on all 21, including the three caustic-soda-flakes pages that
  // share a spec-table band with the live /product/caustic-soda-flakes-188 page
  // (user decision). They target regional supplier intent while that page targets
  // product intent, and only 1 of 9 bands overlaps — pointing these at /product/
  // would de-index the regional copy they exist for.
  return {
    props: {
      config,
      canonical,
      schema: buildLandingPageSchema(config, canonical),
    },
  };
}
